import { createServerFn } from '@tanstack/react-start';
import { getCookie } from '@tanstack/react-start/server';

import { Roles } from '@/api/core-api';
import { isResearcherV3Enabled } from '@/utils/feature-flags-server';

import { SESSION_COOKIE_NAME } from './constants';
import { getServerSession } from './get-server-session';
import {
  clearLastActiveDashboard,
  writeLastActiveDashboard,
} from './last-active-dashboard-cookie';
import { parseSessionCookie } from './parse-cookie';
import { updateSessionState } from './update-session-state';

/**
 * Discriminated result so callers can render targeted error messages
 * (e.g. "not a member of that team") instead of a generic failure toast.
 *
 * Failure reasons:
 *  - `unauthenticated`  — no session cookie, or the session expired between
 *    the cookie read and the Redis write.
 *  - `forbidden-role`   — caller is not a researcher.
 *  - `forbidden-org`    — researcher is not a member of `organizationId`.
 *  - `forbidden-team`   — researcher is not a member of `teamId` in that org.
 */
export type PersistActiveDashboardResult =
  | {
      success: true;
      activeDashboard: { organization: string; team: string | null };
      v3Enabled: boolean;
    }
  | {
      success: false;
      reason:
        | 'unauthenticated'
        | 'forbidden-role'
        | 'forbidden-org'
        | 'forbidden-team';
    };

/**
 * Server function that writes `{ organization, team }` into the current
 * session's `activeDashboard`. Used by:
 *
 *  - `/researchers/select-team` after the user picks from the team-switcher UI
 *    (called from a `useMutation` in the React component).
 *  - The `_authenticated.tsx` auto-persist `beforeLoad` path when the user
 *    has exactly one org + one team and we want to skip showing the picker.
 *
 * Membership validation runs unconditionally and reads `session.user`, which
 * `getServerSession` just fetched fresh from core-api. This is what protects
 * against a researcher writing an org/team they don't belong to into their
 * own session (the picker call goes through the same path as the BFF used
 * to). The trusted-server-caller path pays the cost of one in-memory
 * `.some(...)` check, which is negligible.
 *
 * Membership is enforced against `session.user.organizations` /
 * `session.user.teams` directly so an admin-impersonated session also can't
 * sneak in: the impersonated user document is what's loaded.
 */
export const persistActiveDashboard = createServerFn()
  .validator((data: { organizationId: string; teamId: string | null }) => data)
  .handler(
    async ({
      data: { organizationId, teamId },
    }): Promise<PersistActiveDashboardResult> => {
      const session = await getServerSession();
      if (!session?.user) {
        return { success: false, reason: 'unauthenticated' };
      }

      if (!session.user.roles?.includes(Roles.RESEARCHER)) {
        return { success: false, reason: 'forbidden-role' };
      }

      // A rejection here is also the moment we learn a remembered workspace is
      // stale (the membership was revoked since it was written). Drop it, or
      // the resume in `resolveActiveDashboard` retries the same doomed write
      // on every login.
      const belongsToOrg = (session.user.organizations ?? []).some(
        (o) => o.organizationId === organizationId,
      );
      if (!belongsToOrg) {
        clearLastActiveDashboard();
        return { success: false, reason: 'forbidden-org' };
      }

      if (teamId) {
        const belongsToTeam = (session.user.teams ?? []).some(
          (t) => t.teamId === teamId && t.organizationId === organizationId,
        );
        if (!belongsToTeam) {
          clearLastActiveDashboard();
          return { success: false, reason: 'forbidden-team' };
        }
      }

      // Resolve the underlying session id from the signed cookie so we write
      // to the same Redis record `getServerSession` just read.
      const sessionCookie = getCookie(SESSION_COOKIE_NAME);
      const sessionId = sessionCookie
        ? parseSessionCookie(sessionCookie)
        : null;
      if (!sessionId) {
        return { success: false, reason: 'unauthenticated' };
      }

      const v3Enabled = await isResearcherV3Enabled(
        session.user.id,
        organizationId,
      );
      const updated = await updateSessionState(
        sessionId,
        { activeDashboard: { organization: organizationId, team: teamId } },
        { v3Enabled },
      );
      if (!updated) {
        return { success: false, reason: 'unauthenticated' };
      }

      // Remember the choice for next time. Every route into an active
      // dashboard lands here — the picker, and the single-team auto-persist —
      // so this one call covers both without the client writing anything.
      if (teamId) {
        writeLastActiveDashboard(session.user.id, {
          organizationId,
          teamId,
        });
      }

      return {
        success: true,
        activeDashboard: { organization: organizationId, team: teamId },
        v3Enabled,
      };
    },
  );
